import { v4 as uuidv4 } from 'uuid';
import { nextTick } from 'vue';
import { store } from '@/app/store';
import { ERROR_FALLBACKS, useUiStore } from '@/app/stores/ui';
import { restApi } from '@/services/api';
import { kioskJourney } from '@/services/telemetry/kiosk-journey';
import { sentryService } from '@/services/telemetry/sentry';
import { PubSub } from '@/shared/lib/pub-sub';
import { reactive } from '@/shared/lib/reactive';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
import extractObjectDataProperties from '@/shared/utils/extract-object-data-properties';
import { fetchAuthenticatedImage } from '@/shared/utils/fetch-authenticated-image';
import { track } from '@/shared/utils/track-telemetry';

// How many people one session carries: the visitor selects up to this many.
export const MAX_FACES = 3;
// How many faces the Faces screen offers, largest first. The display cap and
// the selection cap are different: a crowd photo can hold more people than a
// session takes, and the visitor selects who goes into the session.
export const MAX_FACES_DISPLAYED = 8;

function bboxArea(bbox) {
  return (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]);
}

export class FaceDetection extends PubSub {
  // Maximum time (ms) to wait for the server-side face detection API to respond.
  // If exceeded, the upload is aborted and the user is shown a retry prompt.
  // Successful uploads complete in ~2.5s. The upload endpoint is retryable
  // (up to 3 attempts with exponential backoff: 500ms + 1s = 1.5s overhead),
  // so the timeout must accommodate retries on slow kiosk 4G/WiFi networks.
  // 30s allows ~2 full retry cycles on a slow connection while still failing
  // faster than the Http-level 120s timeout.
  UPLOAD_TIMEOUT_MS = 30_000;

  @reactive
  photos = [];

  @reactive
  selectedFaceUuids = [];

  @reactive
  nextStep = null;

  @reactive
  hasSkippedMobileUploads = false;

  // Incremented on reset() to invalidate in-flight async operations
  // (e.g., _convertBlobsToDataUrls) that should not modify state after session ends
  _resetGeneration = 0;

  // Guard against concurrent upload() calls.  On the kiosk, rapid taps on
  // "Use Photo" or overlapping webcam + mobile uploads could fire upload()
  // twice before the first one resolves, causing interleaved state mutations.
  _uploadInProgress = false;

  get faces() {
    const allFaces = this.photos.flatMap((photo) => photo.faces);

    // Sort by bbox area (descending) - larger faces first
    const sortedFaces = allFaces.sort((a, b) => {
      const areaA = a.bbox ? bboxArea(a.bbox) : 0;
      const areaB = b.bbox ? bboxArea(b.bbox) : 0;
      return areaB - areaA;
    });

    // A display cap only. Generation, printing and telemetry read
    // facesSelected, which changeSelectedFaces holds to MAX_FACES.
    return sortedFaces.slice(0, MAX_FACES_DISPLAYED);
  }

  get facesSelected() {
    return this.faces.filter((face) =>
      this.selectedFaceUuids.includes(face.uuid)
    );
  }

  get photosUploaded() {
    return this.queryPhotos({ uploaded: true });
  }

  isWatchingMobileUploads = false;
  watchAbortController = null;

  EVENTS = {
    NEW_PHOTOS_UPLOADED: 'NEW_PHOTOS_UPLOADED',
  };

  SOURCES = {
    WEBCAM: 'webcam',
    MOBILE: 'mobile',
  };

  constructor() {
    super();
    exposeToGlobalScope(this, 'FaceDetection');
    this.updateNextStep();
  }

  queryPhotos(options) {
    const { uploaded, webcam, mobile, index } = options;
    const photos = this.photos.filter((photo) => {
      let sourceMatch = true;
      if ('webcam' in options) {
        sourceMatch =
          Boolean(webcam) === (photo.source === this.SOURCES.WEBCAM);
      } else if ('mobile' in options) {
        sourceMatch =
          Boolean(mobile) === (photo.source === this.SOURCES.MOBILE);
      }
      return (
        sourceMatch &&
        (!('uploaded' in options) || Boolean(uploaded) === photo.isUploaded)
      );
    });
    if (index !== undefined) {
      return photos[index];
    }
    return photos;
  }

  addPhotos(rawInput, source) {
    const rawInputArray = Array.isArray(rawInput) ? rawInput : [rawInput];
    const isBlobArray = rawInputArray[0] instanceof Blob;

    const addedPhotos = rawInputArray.map((input) => {
      const rawPhoto = isBlobArray
        ? { blob: input, source }
        : { ...input, source: input.source || source }; // Preserve backend source if exists

      const normalizedPhoto = this.normalizePhoto(rawPhoto);
      this.photos.push(normalizedPhoto);
      return normalizedPhoto;
    });

    // Track photo addition
    sentryService.addBreadcrumb(
      'face_detection',
      `Added ${addedPhotos.length} photo(s) from ${source}`,
      {
        count: addedPhotos.length,
        source,
        total_photos: this.photos.length,
      }
    );

    return addedPhotos;
  }

  addPhotosFromWebcam(rawInput) {
    const photos = this.addPhotos(rawInput, this.SOURCES.WEBCAM);
    // Replace the blob URLs with data URLs in the background.
    this._convertBlobsToDataUrls(photos);
    return photos;
  }

  addPhotosFromMobile(rawInput) {
    const photos = this.addPhotos(rawInput, this.SOURCES.MOBILE);
    // Replace the blob URLs with data URLs in the background.
    this._convertBlobsToDataUrls(photos);
    return photos;
  }

  /**
   * Replace the blob URL of each photo with a data URL, and revoke the blob URL.
   * For a data URL, a URL.revokeObjectURL() call is not necessary.
   * Sentry Replay does not record these photos, because the elements that show
   * them have the sentry-block class.
   * @param {Array} photos - Photos to convert
   * @private
   */
  async _convertBlobsToDataUrls(photos) {
    // Capture generation at call time to detect if reset() runs during conversion
    const generation = this._resetGeneration;

    for (const photo of photos) {
      // Bail out if session was reset since this conversion started
      if (this._resetGeneration !== generation) {
        return;
      }

      if (photo.blob && photo.blobUrl?.startsWith('blob:')) {
        try {
          const dataUrl = await this._blobToDataUrl(photo.blob);

          // Re-check after async gap -- reset() may have run during conversion
          if (this._resetGeneration !== generation) {
            return;
          }

          // Revoke the old blob URL to prevent memory leak
          URL.revokeObjectURL(photo.blobUrl);
          photo.blobUrl = dataUrl;
          photo._blobUrlNeedsCleanup = false; // Data URLs are auto-GC'd
        } catch (e) {
          // Non-critical - keep blob URL if conversion fails
          console.warn(
            '[FaceDetection] Failed to convert blob to data URL:',
            e.message
          );
        }
      }
    }
  }

  /**
   * Convert a Blob to a data URL.
   * Includes a safety timeout to prevent hanging on corrupt blobs (24/7 kiosk).
   * @param {Blob} blob
   * @param {number} [timeoutMs=15000] - Maximum time before aborting the read
   * @returns {Promise<string>}
   * @private
   */
  _blobToDataUrl(blob, timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        reader.abort();
        reject(new Error('Blob to data URL conversion timed out'));
      }, timeoutMs);

      reader.onloadend = () => {
        clearTimeout(timer);
        if (!timedOut) {
          if (reader.result) {
            resolve(reader.result);
          } else {
            reject(
              new Error('Failed to convert blob to data URL: empty result')
            );
          }
        }
      };
      reader.onerror = () => {
        clearTimeout(timer);
        if (!timedOut) {
          reject(new Error('FileReader error'));
        }
      };
      reader.readAsDataURL(blob);
    });
  }

  async upload(photos, animateFromRef) {
    const photosArray = Array.isArray(photos) ? photos : [photos];
    const uploadStartTime = performance.now();

    // Determine source based on photo source
    const firstSource = photosArray[0]?.source;
    const source =
      firstSource === this.SOURCES.WEBCAM || firstSource === this.SOURCES.MOBILE
        ? firstSource
        : null;

    // Prevent concurrent uploads from interleaving state mutations.
    // On a kiosk, double-taps or overlapping webcam + mobile uploads can
    // fire upload() before the previous one resolves.
    if (this._uploadInProgress) {
      console.warn(
        '[FaceDetection] Upload already in progress, ignoring concurrent call'
      );
      sentryService.addBreadcrumb(
        'face_detection',
        'Concurrent upload rejected',
        { source, photo_count: photosArray.length },
        'warning'
      );
      return [];
    }
    this._uploadInProgress = true;

    // Calculate total upload size for diagnostics.
    // This directly correlates with upload duration on slow kiosk networks.
    const totalBlobBytes = photosArray.reduce(
      (sum, photo) => sum + (photo.blob?.size || 0),
      0
    );

    sentryService.addBreadcrumb(
      'face_detection',
      'Starting photo upload with face detection',
      {
        photo_count: photosArray.length,
        source,
        total_blob_bytes: totalBlobBytes,
        total_blob_kb: Math.round(totalBlobBytes / 1024),
      }
    );

    // Capture generation to detect if reset() runs mid-upload.
    // If the generation changes, upload results belong to a stale session
    // and must not be applied to the (now-cleared) state.
    const generation = this._resetGeneration;

    // Core upload logic extracted for resilient execution
    const analyticsVisitId = kioskJourney.id;
    const executeUpload = async () => {
      // Add timing before API call
      const apiCallStartTime = performance.now();

      // Wrap the detection API call in its own span, with a timeout to prevent
      // the kiosk from hanging forever if the server is unresponsive.
      const apiPromise = sentryService.withSpan(
        'face_detection.api_call',
        'http.client',
        async () => {
          return restApi.uploadPhotosWithFaceRecognition(photosArray, source);
        },
        {
          'face_detection.source': source,
          'face_detection.photo_count': photosArray.length,
        }
      );

      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          // Abort the in-flight fetch so the TCP connection is closed immediately.
          // Without this, the fetch continues for another 60s (until Http.REQUEST_TIMEOUT_MS),
          // wasting backend resources and generating a second error event.
          restApi.abortUploadRequests();

          const err = new Error(
            `Face detection upload timed out after ${this.UPLOAD_TIMEOUT_MS}ms`
          );
          err.name = 'TimeoutError';
          err._isKioskError = true;
          reject(err);
        }, this.UPLOAD_TIMEOUT_MS);
      });

      // Attach a no-op catch to apiPromise to prevent unhandled rejection.
      // When the timeout fires, it aborts the fetch, which causes apiPromise
      // to reject with AbortError. Since Promise.race already settled with
      // TimeoutError, the AbortError rejection would be unhandled.
      apiPromise.catch(() => {});

      let response;
      try {
        response = await Promise.race([apiPromise, timeoutPromise]);
      } finally {
        // Clear timeout to prevent leaked timer on the 24/7 kiosk
        clearTimeout(timeoutId);
      }

      // Track API call duration separately
      const apiCallDuration = performance.now() - apiCallStartTime;
      sentryService.trackMetric(
        'face_detection.api_duration_ms',
        apiCallDuration,
        'millisecond',
        { source }
      );

      // Bail out if session was reset while the API call was in flight.
      // Applying stale results would pollute the new (empty) session.
      if (this._resetGeneration !== generation) {
        console.warn(
          '[FaceDetection] Upload result discarded: session was reset during upload'
        );
        return [];
      }

      const preloadedResponse = await this.preloadPhotosFromApiResponseIfNeeded(
        response,
        generation
      );

      // Re-check after preload -- another async gap where reset() could fire
      if (this._resetGeneration !== generation) {
        console.warn(
          '[FaceDetection] Upload result discarded: session was reset during preload'
        );
        return [];
      }

      const updatedPhotos = preloadedResponse
        .map((entry) => {
          // Use temp_uuid to find the correct photo to update
          const tempUuid = entry.temp_uuid || entry.uuid;
          return this.updatePhoto(tempUuid, {
            uuid: entry.uuid, // Update with backend-generated UUID
            serverUrl: entry.public_link,
            faces: entry.faces,
            animateFromRef,
          });
        })
        .filter(Boolean); // Drop nulls from photos removed during async upload

      // Calculate total faces detected
      const totalFacesDetected = updatedPhotos.reduce(
        (sum, photo) => sum + (photo.faces?.length || 0),
        0
      );
      kioskJourney.record('photo_received', {
        source,
        photos_count: updatedPhotos.length,
        faces_detected_count: totalFacesDetected,
      });

      // Track face detection metrics for Sentry
      const photosWithZeroFaces = updatedPhotos.filter(
        (photo) => !photo.faces || photo.faces.length === 0
      ).length;
      const avgFacesPerPhoto =
        photosArray.length > 0 ? totalFacesDetected / photosArray.length : 0;

      track('[FaceDetection] Sentry call failed:', () => {
        sentryService.trackMetric(
          'face_detection.faces_detected_total',
          totalFacesDetected,
          'none',
          { source }
        );
        sentryService.trackMetric(
          'face_detection.faces_per_photo_avg',
          avgFacesPerPhoto,
          'none',
          { source }
        );
        sentryService.trackMetric(
          'face_detection.photos_with_zero_faces',
          photosWithZeroFaces,
          'none',
          { source }
        );

        // Track confidence scores
        const confidenceScores = updatedPhotos
          .flatMap((photo) => photo.faces || [])
          .map((face) => face.confidence)
          .filter((c) => c != null && !isNaN(c));

        if (confidenceScores.length > 0) {
          const avgConfidence =
            confidenceScores.reduce((a, b) => a + b, 0) /
            confidenceScores.length;
          const minConfidence = Math.min(...confidenceScores);

          sentryService.trackMetric(
            'face_detection.avg_confidence',
            avgConfidence,
            'ratio',
            { source }
          );
          sentryService.trackMetric(
            'face_detection.min_confidence',
            minConfidence,
            'ratio',
            { source }
          );

          // Flag low confidence detections
          if (minConfidence < 0.7) {
            sentryService.addBreadcrumb(
              'face_detection',
              'Low confidence detection',
              {
                min_confidence: minConfidence,
                avg_confidence: avgConfidence,
                source,
              },
              'warning'
            );
          }
        }

        sentryService.incrementCounter(
          'face_detection.photos_processed',
          photosArray.length,
          { source }
        );

        // Track memory after face detection
        sentryService.trackMemoryUsage('after_face_detection');

        // Update Sentry flow context with photo/face data
        sentryService.setFlowContext({
          photoCount: photosArray.length,
          faceCount: totalFacesDetected,
          selectedFaceCount: 0, // Will be updated when user selects faces
        });

        // Set face detection context with bounding boxes for debugging.
        // Demographic values never go to Sentry: a face carries only the
        // fact that the detector gave each trait.
        const faceContext = updatedPhotos.flatMap((photo, photoIdx) =>
          (photo.faces || []).map((face, faceIdx) => ({
            face_id: `${photoIdx}_${faceIdx}`,
            // Full face UUID is intentional Sentry metadata for image pipeline joins.
            face_uuid: face.uuid,
            bbox: face.bbox,
            bbox_area: face.bbox ? Math.round(bboxArea(face.bbox)) : null,
            has_age_group: Boolean(face.age_group),
            has_gender: Boolean(face.gender),
            has_race: Boolean(face.race),
            confidence: face.confidence,
          }))
        );

        sentryService.setContext('detected_faces', {
          total_faces: faceContext.length,
          faces: faceContext.slice(0, 10), // Limit to prevent huge context
        });
      });

      // Consolidated Sentry tracking - reduced from 5 calls to 1 breadcrumb
      // Removed: trackMetric (span provides timing), incrementCounters (data in breadcrumb)
      // updateSessionContext moved after trigger/updateNextStep to reduce overhead
      const uploadDuration = performance.now() - uploadStartTime;
      sentryService.addBreadcrumb(
        'face_detection',
        `Photo upload completed: ${totalFacesDetected} faces detected`,
        {
          photo_count: photosArray.length,
          faces_detected: totalFacesDetected,
          duration_ms: Math.round(uploadDuration),
          source,
        }
      );

      this.trigger(this.EVENTS.NEW_PHOTOS_UPLOADED, updatedPhotos);
      this.updateNextStep();

      // Update session context outside critical path (deferred, non-blocking)
      try {
        sentryService.updateSessionContext({
          faces_detected: this.faces.length,
          photos_uploaded: this.photosUploaded.length,
        });
      } catch (_e) {
        // Silent fail - context update is non-critical
      }

      return updatedPhotos;
    };

    // Handle upload errors consistently.
    // NOTE: Do NOT call sentryService.captureException here -- it's already
    // captured by RestApi._captureApiError (at the HTTP layer) and again by
    // useUiStore().showError (which captures with error_code context). Adding a third
    // captureException here was causing 3 duplicate Sentry events per upload
    // failure, making it hard to gauge actual error volume.
    const handleUploadError = async (e) => {
      if (e?.name !== 'AbortError' && generation === this._resetGeneration) {
        kioskJourney.record(
          'photo_failed',
          { source, photos_count: photosArray.length },
          analyticsVisitId
        );
      }
      sentryService.addBreadcrumb(
        'face_detection',
        `Photo upload failed: ${e.message}`,
        { source, photo_count: photosArray.length },
        'error'
      );

      useUiStore().showError(
        'face_detection_server_error',
        e,
        ERROR_FALLBACKS.RETRY_CAMERA
      );
      throw e;
    };

    // Try withSpan, fallback to direct execution if Sentry fails
    try {
      return await sentryService.withSpan(
        'face_detection.upload',
        'face_detection',
        executeUpload,
        {
          'face_detection.photo_count': photosArray.length,
          'face_detection.source': source,
        }
      );
    } catch (e) {
      // Distinguish between Sentry infrastructure errors (withSpan itself
      // broke) vs actual upload/network errors that propagated through
      // withSpan's callback.
      //
      // Actual errors from the upload pipeline have one or more of:
      // - _isKioskError: set by FaceDetection timeout
      // - .context: set by ParsedError from Http/ErrorParser layer
      // - name === 'TimeoutError' / 'ParsedError': Http or FD timeout
      //
      // Sentry infrastructure errors (e.g., "Cannot read property of
      // undefined" inside Sentry SDK) have none of these markers.
      // ParsedError must be matched too: server errors (500) wrapped in
      // ParsedError were otherwise treated as "Sentry error" and the code
      // fell through to a DOUBLE upload attempt.
      const isUploadError =
        e._isKioskError ||
        e.context != null ||
        e.name === 'TimeoutError' ||
        e.name === 'ParsedError' ||
        e.name === 'TypeError' ||
        e.message?.includes('fetch') ||
        e.message?.includes('network') ||
        e.message?.includes('timeout') ||
        e.message?.includes('abort');

      if (isUploadError) {
        await handleUploadError(e);
      } else {
        // This might be a Sentry error, try executing without tracing
        console.warn(
          '[FaceDetection] Sentry withSpan failed, continuing without tracing:',
          e.message
        );
        try {
          return await executeUpload();
        } catch (uploadError) {
          await handleUploadError(uploadError);
        }
      }
    } finally {
      this._uploadInProgress = false;
    }
  }

  async watchMobileUploads(options) {
    const { pollingInterval = 5000, preload } = options || {};

    // Abort any existing watch loop
    this.unwatchMobileUploads();

    // Create new abort controller
    this.watchAbortController = new AbortController();
    const signal = this.watchAbortController.signal;

    this.isWatchingMobileUploads = true;

    sentryService.addBreadcrumb(
      'face_detection',
      'Started watching for mobile uploads',
      { polling_interval_ms: pollingInterval, preload }
    );

    try {
      while (this.isWatchingMobileUploads) {
        // Check if aborted before API call
        if (signal.aborted) {
          break;
        }

        const response = await restApi.getUploadedPhotosInSessionSilent();

        // Check again after async operation
        if (signal.aborted) {
          break;
        }

        // Filter out photos that are already known AND filter out webcam photos
        const responseNewEntries = response.filter(
          (entry) =>
            !this.getPhotoByUuid(entry.uuid) && entry.source !== 'webcam'
        );

        if (responseNewEntries.length > 0) {
          // Track new mobile photos found
          sentryService.addBreadcrumb(
            'face_detection',
            `Found ${responseNewEntries.length} new mobile photo(s)`,
            {
              new_photo_count: responseNewEntries.length,
              faces_detected: responseNewEntries.reduce(
                (sum, e) => sum + (e.faces?.length || 0),
                0
              ),
            }
          );

          if (preload) {
            try {
              await this.preloadPhotosFromApiResponseIfNeeded(
                responseNewEntries
              );
            } catch (preloadErr) {
              // Preload failure should not kill the polling loop.
              // Photos will still be added below without preloaded blob URLs,
              // falling back to server URLs for display.
              console.warn(
                '[FaceDetection] Preload failed during mobile watch, continuing:',
                preloadErr.message
              );
            }
          }

          // Check abort before updating state
          if (signal.aborted) {
            break;
          }

          if (this.isWatchingMobileUploads) {
            // Mark as uploaded since they came from the backend
            const uploadedEntries = responseNewEntries.map((entry) => ({
              ...entry,
              isUploaded: true,
            }));
            const mobilePhotos = this.addPhotosFromMobile(uploadedEntries);
            kioskJourney.record('photo_received', {
              source: 'mobile',
              photos_count: mobilePhotos.length,
              faces_detected_count: mobilePhotos.reduce(
                (sum, photo) => sum + (photo.faces?.length || 0),
                0
              ),
            });
            this.trigger(this.EVENTS.NEW_PHOTOS_UPLOADED, mobilePhotos);
            this.updateNextStep();

            // Increment mobile photos counter
            sentryService.incrementCounter(
              'face_detection.mobile_photos_received',
              mobilePhotos.length
            );
          }
        }

        // Abortable sleep
        await this.abortableSleep(pollingInterval, signal);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        sentryService.addBreadcrumb(
          'face_detection',
          'Mobile upload watching aborted',
          {},
          'info'
        );
      } else {
        sentryService.captureException(error, {
          tags: { operation: 'watch_mobile_uploads' },
          extra: { polling_interval_ms: pollingInterval },
          level: 'error',
        });
        throw error;
      }
    }
  }

  abortableSleep(ms, signal) {
    return new Promise((resolve, reject) => {
      // Named handler for proper cleanup when timeout fires normally
      const abortHandler = () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      const timeoutId = setTimeout(() => {
        // Remove listener to prevent memory leak when timeout completes normally
        signal.removeEventListener('abort', abortHandler);
        resolve();
      }, ms);

      signal.addEventListener('abort', abortHandler, { once: true });
    });
  }

  unwatchMobileUploads() {
    if (this.isWatchingMobileUploads) {
      sentryService.addBreadcrumb(
        'face_detection',
        'Stopped watching for mobile uploads',
        {}
      );
    }

    this.isWatchingMobileUploads = false;

    // Abort the watching loop immediately
    if (this.watchAbortController) {
      this.watchAbortController.abort();
      this.watchAbortController = null;
    }
  }

  skipMobileUploads() {
    return new Promise((resolve) => {
      this.hasSkippedMobileUploads = true;
      this.updateNextStep();
      nextTick(() => resolve());
    });
  }

  updateNextStep() {
    const hasWebcamPhotos =
      this.queryPhotos({ webcam: true, uploaded: true }).length > 0;
    const hasMobilePhotos =
      this.queryPhotos({ mobile: true, uploaded: true }).length > 0;

    let nextStep = 'photo-from-webcam';

    // If we have ANY uploaded photos, we can proceed
    if (hasWebcamPhotos || hasMobilePhotos) {
      // Only prompt for mobile if we have webcam but no mobile (and haven't skipped) and mobile upload not hidden
      // Only hide if explicitly true, otherwise show (respect DB setting)
      const isMobileUploadHidden = store.kiosk?.hide_mobile_upload === true;
      if (
        hasWebcamPhotos &&
        !hasMobilePhotos &&
        !this.hasSkippedMobileUploads &&
        !isMobileUploadHidden
      ) {
        nextStep = 'photo-prompt-from-phone';
      } else {
        // Go to faces if we have any photos (mobile, webcam, or both)
        nextStep = 'faces';
      }
    }
    // No photos at all - start with webcam
    else {
      nextStep = 'photo-from-webcam';
    }

    this.nextStep = nextStep;
  }

  updatePhoto(uuid, diff) {
    const photo = this.getPhotoByUuid(uuid);
    if (!photo) {
      // Photo was removed from state while an async operation was in flight
      // (user retook photo, navigated back, etc.) — nothing to update.
      return null;
    }
    const dataProps = extractObjectDataProperties(
      this.normalizePhoto({ ...photo, ...diff })
    );
    return Object.assign(photo, dataProps);
  }

  /**
   * The selection holds a maximum of MAX_FACES faces. An add past the limit
   * does nothing, and a set keeps the first MAX_FACES faces.
   * @param {Object} options
   * @param {Array<string>|string|undefined} [options.add] - Faces to add to the selection (in addition to existing ones).
   * @param {Array<string>|string|undefined} [options.remove] - Faces to remove from the existing selection.
   * @param {Array<string>|string|undefined} [options.set] - Faces to set as selected (omitting all currently selected faces).
   * @param {Array<string>|string|undefined} [options.toggle] - Faces whose selection is to be toggled.
   */
  changeSelectedFaces(options) {
    const entry = Object.entries(options).find((entry) => {
      const [_typeRaw, faceUuidsRaw] = entry;
      return Boolean(faceUuidsRaw);
    });

    if (!entry) {
      console.warn(
        '[FaceDetection] changeSelectedFaces called with empty or invalid options'
      );
      return;
    }

    const [typeRaw, faceUuidsRaw] = entry;
    const type = typeRaw.toLowerCase();
    const faceUuidsArray = Array.isArray(faceUuidsRaw)
      ? faceUuidsRaw
      : [faceUuidsRaw];
    const previousCount = this.selectedFaceUuids.length;

    if (type === 'set') {
      this.selectedFaceUuids = faceUuidsArray.slice(0, MAX_FACES);
    } else {
      const selectedFaceUuidsSet = new Set(this.selectedFaceUuids);
      for (const faceUuid of faceUuidsArray) {
        const isAdd =
          type === 'add' ||
          (type === 'toggle' && !selectedFaceUuidsSet.has(faceUuid));
        if (isAdd) {
          if (selectedFaceUuidsSet.size < MAX_FACES) {
            selectedFaceUuidsSet.add(faceUuid);
          }
        } else if (
          type === 'remove' ||
          (type === 'toggle' && selectedFaceUuidsSet.has(faceUuid))
        ) {
          selectedFaceUuidsSet.delete(faceUuid);
        }
      }
      this.selectedFaceUuids = Array.from(selectedFaceUuidsSet);
    }

    // Track face selection changes
    track('[FaceDetection] Sentry call failed:', () => {
      sentryService.addBreadcrumb(
        'face_detection',
        `Face selection changed: ${type}`,
        {
          action: type,
          faces_affected: faceUuidsArray.length,
          previous_selected: previousCount,
          current_selected: this.selectedFaceUuids.length,
        }
      );

      // Update session context with selected faces count
      sentryService.updateSessionContext({
        selected_faces_count: this.selectedFaceUuids.length,
      });

      // Update selected face count in Sentry flow context
      sentryService.setFlowContext({
        selectedFaceCount: this.selectedFaceUuids.length,
      });
    });
  }

  isFaceSelected(faceUuid) {
    return this.selectedFaceUuids.includes(faceUuid);
  }

  getPhotoByUuid(uuid) {
    return this.photos.find((photo) => photo.uuid === uuid);
  }

  normalizePhoto(rawPhoto) {
    // Create blob URL if needed and track if we created it (for cleanup)
    // Prefer preloaded _blobUrl (from authenticated fetch), then existing blobUrl, then create from blob
    const blobUrl =
      rawPhoto._blobUrl ||
      rawPhoto.blobUrl ||
      (rawPhoto.blob && URL.createObjectURL(rawPhoto.blob));
    // Track cleanup needed for blob URLs we created (not preloaded ones, those are tracked separately)
    const needsCleanup =
      !(rawPhoto._blobUrl || rawPhoto.blobUrl) && rawPhoto.blob && blobUrl;

    return {
      ...rawPhoto,
      // Generate temporary UUID only if not provided by backend
      uuid: rawPhoto.uuid || (rawPhoto.blob ? uuidv4() : null),
      blob: rawPhoto.blob,
      blobUrl: blobUrl,
      _blobUrlNeedsCleanup: needsCleanup,
      _preloadedBlobUrl: rawPhoto._blobUrl, // Track preloaded URLs for cleanup
      serverUrl: rawPhoto.serverUrl || rawPhoto.public_link,
      faces: (rawPhoto.faces || []).map(this.normalizeFace),
      animateFromRef: rawPhoto.animateFromRef,
      get isUploaded() {
        return Boolean(this.serverUrl);
      },
      get url() {
        // Use the preloaded data URL first, then the local blobUrl, then the server URL.
        return this._preloadedBlobUrl || this.blobUrl || this.serverUrl;
      },
    };
  }

  normalizeFace(rawFace) {
    // Capture confidence score from various possible field names.
    // Use ?? (not ||) so that a valid confidence of 0 is preserved.
    let confidence =
      rawFace.confidence ?? rawFace.det_score ?? rawFace.score ?? null;
    // Validate: clamp to [0, 1] or null out garbage values.
    // Backend may return scores >1 (e.g., raw logits) or negative values.
    if (confidence !== null) {
      confidence = Number(confidence);
      if (Number.isNaN(confidence) || !Number.isFinite(confidence)) {
        confidence = null;
      } else {
        confidence = Math.max(0, Math.min(1, confidence));
      }
    }

    // Validate demographics: guard against partial/garbage backend responses.
    // Default to null rather than undefined to prevent downstream errors
    // when building API payloads (e.g., submitGeneration expects strings or null).
    const age_group = rawFace.age_group || null;
    const gender = rawFace.gender || null;
    const race = rawFace.race || null;

    return {
      // Only use backend-provided UUID, no fallback generation
      uuid: rawFace.uuid,
      // Prefer blob URL (authenticated fetch), fallback to original URL
      url: rawFace._blobUrl || rawFace.url || rawFace.public_link,
      _blobUrl: rawFace._blobUrl,
      _originalUrl: rawFace.url || rawFace.public_link,
      age_group,
      gender,
      race,
      // Preserve bbox for sorting by face size [x1, y1, x2, y2]
      bbox: rawFace.bbox || null,
      confidence,
    };
  }

  async preloadPhotosFromApiResponseIfNeeded(response, generation) {
    // Accept generation from caller for stale-session detection.
    // If not provided (e.g., from watchMobileUploads), snapshot current generation.
    const gen = generation ?? this._resetGeneration;

    const photosToPreload = response.filter(
      (photo) => !this.getPhotoByUuid(photo.uuid)?.blobUrl
    );

    if (photosToPreload.length === 0) {
      return response;
    }

    const totalFaces = photosToPreload.reduce(
      (sum, photo) => sum + (photo.faces?.length || 0),
      0
    );

    // Core preload logic extracted for resilient execution
    const executePreload = async () => {
      let successfulPhotos = 0;
      let failedPhotos = 0;
      let successfulFaces = 0;
      let failedFaces = 0;

      await Promise.all(
        photosToPreload.map(async (photo) => {
          // Skip if session was reset during preload
          if (this._resetGeneration !== gen) return;

          // Fetch the photo as a data URL. For a data URL, a
          // URL.revokeObjectURL() call is not necessary.
          if (photo.public_link) {
            try {
              photo._blobUrl = await fetchAuthenticatedImage(photo.public_link);
              successfulPhotos++;
            } catch (e) {
              console.warn(
                `[FaceDetection] Failed to preload photo ${photo.uuid}:`,
                e
              );
              failedPhotos++;
            }
          }

          // Fetch face images
          if (photo.faces && photo.faces.length > 0) {
            await Promise.all(
              photo.faces.map(async (face) => {
                // Skip if session was reset
                if (this._resetGeneration !== gen) return;
                const faceUrl = face.url || face.public_link;
                if (faceUrl) {
                  try {
                    // Fetch the face crop as a data URL, the same as the photo.
                    face._blobUrl = await fetchAuthenticatedImage(faceUrl);
                    successfulFaces++;
                  } catch (e) {
                    console.warn(
                      `[FaceDetection] Failed to preload face ${face.uuid}:`,
                      e
                    );
                    failedFaces++;
                  }
                }
              })
            );
          }
        })
      );

      sentryService.addBreadcrumb(
        'face_detection',
        `Preloaded images: ${successfulPhotos}/${photosToPreload.length} photos, ${successfulFaces}/${totalFaces} faces`,
        {
          photos_success: successfulPhotos,
          photos_failed: failedPhotos,
          faces_success: successfulFaces,
          faces_failed: failedFaces,
        }
      );

      return response;
    };

    // Handle preload errors consistently
    // NOTE: Do NOT call sentryService.captureException here -- useUiStore().showError
    // already captures it. Adding one here caused duplicate Sentry events.
    const handlePreloadError = async (e) => {
      sentryService.addBreadcrumb(
        'face_detection',
        `Preload failed: ${e.message}`,
        { photo_count: photosToPreload.length, face_count: totalFaces },
        'error'
      );
      useUiStore().showError('face_detection_preload_error', e);
      throw e;
    };

    // Try withSpan, fallback to direct execution if Sentry fails
    try {
      return await sentryService.withSpan(
        'face_detection.preload_images',
        'face_detection',
        executePreload,
        {
          'preload.photo_count': photosToPreload.length,
          'preload.face_count': totalFaces,
        }
      );
    } catch (e) {
      // Same classification logic as upload() -- see comment there.
      // Errors from the preload pipeline have .context (ParsedError),
      // _isKioskError, or recognizable error names/messages.
      const isPreloadError =
        e._isKioskError ||
        e.context != null ||
        e.name === 'TimeoutError' ||
        e.name === 'ParsedError' ||
        e.name === 'TypeError' ||
        e.message?.includes('fetch') ||
        e.message?.includes('network') ||
        e.message?.includes('timeout') ||
        e.message?.includes('abort');

      if (isPreloadError) {
        await handlePreloadError(e);
      } else {
        // This might be a Sentry error, try executing without tracing
        console.warn(
          '[FaceDetection] Sentry withSpan failed, continuing without tracing:',
          e.message
        );
        try {
          return await executePreload();
        } catch (preloadError) {
          await handlePreloadError(preloadError);
        }
      }
    }
  }

  cleanupPhoto(photo) {
    let revokedCount = 0;

    // Clean up blob URL created from local blob.
    // After _convertBlobsToDataUrls runs, blobUrl may be a data: URL, which
    // does not need revoking. The _blobUrlNeedsCleanup flag is already cleared
    // by that conversion, but double-check the scheme to be safe.
    if (
      photo._blobUrlNeedsCleanup &&
      photo.blobUrl &&
      photo.blobUrl.startsWith('blob:')
    ) {
      URL.revokeObjectURL(photo.blobUrl);
      revokedCount++;
    }
    photo.blobUrl = null;
    photo._blobUrlNeedsCleanup = false;

    // fetchAuthenticatedImage gives the preloaded photo and face URLs. They
    // are data: URLs, thus a revoke call is not necessary.
    photo._preloadedBlobUrl = null;

    if (photo.faces) {
      for (const face of photo.faces) {
        if (face._blobUrl) {
          face._blobUrl = null;
        }
        // Release face URL reference to allow GC of data URL strings
        face.url = null;
        face._originalUrl = null;
      }
    }

    // Release large Blob reference to allow GC of raw image data.
    // Without this, multi-MB photo blobs linger in memory across sessions.
    photo.blob = null;

    // Track cleanup
    if (revokedCount > 0) {
      sentryService.incrementCounter(
        'face_detection.blob_urls_revoked',
        revokedCount
      );
    }
  }

  reset() {
    // Invalidate any in-flight async operations (e.g., _convertBlobsToDataUrls)
    this._resetGeneration++;

    // Release upload lock so the next session can upload.
    // The in-flight upload (if any) will detect the generation change and bail.
    this._uploadInProgress = false;

    const previousPhotoCount = this.photos.length;
    const previousFaceCount = this.faces.length;
    const previousSelectedCount = this.selectedFaceUuids.length;

    // Track memory before cleanup
    const memoryBefore = performance.memory?.usedJSHeapSize;

    // Clean up all blob URLs before clearing
    for (const photo of this.photos) {
      this.cleanupPhoto(photo);
    }

    // Abort mobile upload polling loop before clearing state
    this.unwatchMobileUploads();

    this.photos = [];
    this.selectedFaceUuids = [];
    this.hasSkippedMobileUploads = false;
    this.updateNextStep();
    this.resetPubSub();

    // Track memory after cleanup
    if (memoryBefore && performance.memory) {
      const memoryFreed = memoryBefore - performance.memory.usedJSHeapSize;
      if (memoryFreed > 0) {
        sentryService.trackMetric(
          'face_detection.memory_freed_mb',
          Math.round(memoryFreed / 1024 / 1024),
          'megabyte'
        );
      }
    }

    track('[FaceDetection] Sentry call failed:', () => {
      sentryService.addBreadcrumb(
        'face_detection',
        'Face detection state reset',
        {
          cleared_photos: previousPhotoCount,
          cleared_faces: previousFaceCount,
          cleared_selected: previousSelectedCount,
        }
      );

      // Clear face detection context from session
      sentryService.updateSessionContext({
        faces_detected: 0,
        photos_uploaded: 0,
        selected_faces_count: 0,
      });
    });
  }
}

export const faceDetection = new FaceDetection();
